import { useState } from "react"
import { Button } from "@/components/ui/button"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft, Check } from "lucide-react"
import Navigation from "@/components/Navigation"
import { useSearchParams } from "react-router-dom"

const products = {
  "1": {
    name: "Le Pix",
    format: "13 x 13 cm",
    image: "/images/products/pix.png",
    originalPrice: "20€",
    price: "16€",
    discount: "-20%"
  },
  "2": {
    name: "L'Album Carré",
    format: "21 x 21 cm", 
    image: "/images/products/carre.png",
    originalPrice: "39,99€",
    price: "27,99€",
    discount: "-20%"
  },
  "3": {
    name: "L'Album Paysage",
    format: "A4",
    image: "/images/products/paysage.png", 
    originalPrice: "46,99€",
    price: "37,59€",
    discount: "-20%"
  }
}

export default function CreateProduct() {
  const [searchParams] = useSearchParams()
  const productId = searchParams.get("open") || "1"
  const product = products[productId as keyof typeof products] || products["1"]
  
  const [selectedFormat, setSelectedFormat] = useState("A4")

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Main Content */}
      <main className="pt-20 pb-16">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-display font-bold text-foreground mb-2">
              {product.name} - <span className="text-gradient">Rigide</span>
            </h1>
            <p className="text-xl text-muted-foreground">{product.format}</p>
          </div>

          {/* Price Section */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <Badge className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 text-base font-bold">
              {product.discount}
            </Badge>
            <span className="text-lg text-muted-foreground">
              A partir de <span className="text-2xl font-bold text-foreground">{product.price}</span> au lieu de <span className="line-through">{product.originalPrice}</span>
            </span>
          </div>

          {/* Main Content - Two Columns */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Left Column - Product Preview */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative bg-white rounded-2xl shadow-elegant p-8 max-w-md w-full">
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-center mb-4">
                    <h3 className="text-2xl font-display font-bold text-gray-800">AVENTURE</h3>
                    <p className="text-sm text-gray-600 uppercase tracking-wider">PORTFOLIO</p>
                  </div>
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <img 
                      src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&q=80"
                      alt="Preview album aventure"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Format Selection & Action */}
            <div className="space-y-8">
              {/* Format Selection */}
              <div className="bg-card rounded-2xl shadow-card border border-border/50 p-8">
                <h2 className="text-2xl font-display font-bold text-center mb-8">
                  Choisissez votre format
                </h2>
                
                <RadioGroup 
                  value={selectedFormat} 
                  onValueChange={setSelectedFormat}
                  className="flex justify-center gap-8"
                >
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem 
                      value="A4" 
                      id="A4"
                      className="w-6 h-6 border-2 border-primary data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <Label 
                      htmlFor="A4" 
                      className="text-lg font-medium cursor-pointer flex items-center gap-2"
                    >
                      A4
                      {selectedFormat === "A4" && <Check className="w-4 h-4 text-primary" />}
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem 
                      value="A5" 
                      id="A5"
                      className="w-6 h-6 border-2 border-primary data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <Label 
                      htmlFor="A5" 
                      className="text-lg font-medium cursor-pointer flex items-center gap-2"
                    >
                      A5
                      {selectedFormat === "A5" && <Check className="w-4 h-4 text-primary" />}
                    </Label>
                  </div>
                </RadioGroup>
                
                {/* Additional Info */}
                <div className="mt-6 text-center">
                  <p className="text-muted-foreground">
                    Format sélectionné: <span className="font-semibold text-foreground">{selectedFormat}</span>
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <div className="text-center">
                <Button 
                  size="lg"
                  variant="rainbow"
                  className="px-12 py-4 text-lg font-medium shadow-elegant hover:shadow-glow transition-all duration-300"
                >
                  📖 Commencer mon album
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}